import React, { useEffect, useState } from 'react';
import { Plus, X, Upload } from 'lucide-react';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

export const BasicInfoSection = ({
  name,
  setName,
  slug,
  setSlug,
  logo,
  setLogo,
  tagline,
  setTagline,
  description,
  setDescription,
  country,
  setCountry,
  foundedYear,
  setFoundedYear,
  website,
  setWebsite,
  affiliateUrl,
  setAffiliateUrl,
  platforms,
  setPlatforms,
  instruments,
  setInstruments,
  paymentMethods,
  setPaymentMethods,
  generateId,
}) => {
  const [preview, setPreview] = useState(null);

  // Build preview from File or existing string URL / base64
  useEffect(() => {
    if (!logo) {
      setPreview(null);
      return;
    }

    if (logo instanceof File) {
      const url = URL.createObjectURL(logo);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }

    if (typeof logo === 'string') {
      setPreview(logo);
    }
  }, [logo]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Must store File object for FormData upload
    setLogo(file);
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setPreview(null);
  };

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardContent className="p-6 space-y-6">
        <div>
          <Label>Firm Name *</Label>
          <Input
            placeholder="Enter firm name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
            }}
            className="mt-2"
          />
        </div>

        <div>
          <Label>URL Slug</Label>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-slate-500">propfirmhouse.com/firms/</span>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
        </div>

        {/* Logo — stores File, not base64 */}
        <div>
          <Label>Logo</Label>
          <div className="mt-2 max-w-xs">
            {preview ? (
              <div className="relative inline-block">
                <img
                  src={preview}
                  alt="Logo preview"
                  className="h-32 w-32 object-contain rounded-lg border border-slate-200 bg-slate-50"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-7 w-7 rounded-full"
                  onClick={handleRemoveLogo}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 w-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors">
                <Upload className="h-6 w-6 text-slate-400 mb-1" />
                <span className="text-xs text-slate-500">Upload logo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
            <p className="text-xs text-slate-500 mt-1">Recommended: 200x200px</p>
          </div>
        </div>

        <div>
          <Label>Tagline</Label>
          <Input
            placeholder="Short tagline"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            maxLength={100}
            className="mt-2"
          />
          <p className="text-xs text-slate-500 mt-1">{tagline.length}/100 characters</p>
        </div>

        <div>
          <Label>Description</Label>
          <div className="mt-2">
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Write firm description..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Country</Label>
            <Input
              placeholder="United States"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="mt-2"
            />
          </div>
          <div>
            <Label>Founded Year</Label>
            <Input
              type="number"
              placeholder="2020"
              value={foundedYear}
              onChange={(e) => setFoundedYear(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>

        <div>
          <Label>Website URL</Label>
          <Input
            type="url"
            placeholder="https://firm.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="mt-2"
          />
        </div>

        <div>
          <Label>Affiliate URL</Label>
          <Input
            type="url"
            placeholder="https://firm.com/?ref=propfirmhouse"
            value={affiliateUrl}
            onChange={(e) => setAffiliateUrl(e.target.value)}
            className="mt-2"
          />
        </div>

        <Separator className="my-6" />

        {/* Platforms */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label>Platforms</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setPlatforms([...platforms, { id: generateId(), value: '' }])
              }
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Platform
            </Button>
          </div>
          <div className="space-y-2">
            {platforms.map((platform) => (
              <div key={platform.id} className="flex gap-2">
                <Input
                  placeholder="e.g., MetaTrader 5, cTrader"
                  value={platform.value}
                  onChange={(e) => {
                    setPlatforms(
                      platforms.map((p) =>
                        p.id === platform.id
                          ? { ...p, value: e.target.value }
                          : p
                      )
                    );
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-red-600"
                  onClick={() =>
                    setPlatforms(platforms.filter((p) => p.id !== platform.id))
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {platforms.length === 0 && (
              <p className="text-sm text-slate-500">No platforms added yet</p>
            )}
          </div>
        </div>

        {/* Instruments */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label>Instruments</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setInstruments([...instruments, { id: generateId(), value: '' }])
              }
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Instrument
            </Button>
          </div>
          <div className="space-y-2">
            {instruments.map((instrument) => (
              <div key={instrument.id} className="flex gap-2">
                <Input
                  placeholder="e.g., Forex, Crypto, Indices"
                  value={instrument.value}
                  onChange={(e) => {
                    setInstruments(
                      instruments.map((i) =>
                        i.id === instrument.id
                          ? { ...i, value: e.target.value }
                          : i
                      )
                    );
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-red-600"
                  onClick={() =>
                    setInstruments(
                      instruments.filter((i) => i.id !== instrument.id)
                    )
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {instruments.length === 0 && (
              <p className="text-sm text-slate-500">No instruments added yet</p>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label>Payment Methods</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                setPaymentMethods([
                  ...paymentMethods,
                  { id: generateId(), value: '' },
                ])
              }
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Payment Method
            </Button>
          </div>
          <div className="space-y-2">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex gap-2">
                <Input
                  placeholder="e.g., Credit Card, PayPal, Crypto"
                  value={method.value}
                  onChange={(e) => {
                    setPaymentMethods(
                      paymentMethods.map((m) =>
                        m.id === method.id
                          ? { ...m, value: e.target.value }
                          : m
                      )
                    );
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-red-600"
                  onClick={() =>
                    setPaymentMethods(
                      paymentMethods.filter((m) => m.id !== method.id)
                    )
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {paymentMethods.length === 0 && (
              <p className="text-sm text-slate-500">No payment methods added yet</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};